import { useEffect, useState, type HTMLAttributes } from 'react'
import { Button } from '../../ui/Button'
import { DEFAULT_QUERY, SORT_KEYS, directionLabels, numericFilterValue, pluralHosts, sortKeyLabel, withSortKey, type CatalogQuery, type NumericFilterKey, type SortDirection } from './catalogQuery'

/**
 * The filter-and-sort panel's body.
 *
 * Controls apply as the tenant changes them, as they always have; the submit
 * button only closes the panel. The page renders this inline above the table
 * on a wide screen and inside a bottom sheet on a narrow one.
 */
export function CatalogFilterPanel({ id, query, onChange, onReset, resetEpoch, regions, tiers, resultCount, auditDataAvailable, onDone }: {
  id?: string | undefined
  query: CatalogQuery
  onChange: (next: CatalogQuery) => void
  /** Resets the query and bumps `resetEpoch`, so an unparsed entry is cleared too. */
  onReset: (next: CatalogQuery) => void
  resetEpoch: number
  regions: string[]
  tiers: string[]
  resultCount: number
  auditDataAvailable: boolean
  onDone: () => void
}) {
  const labels = directionLabels(query.sort)
  return <form id={id} className="catalog-filter-form" onSubmit={(event) => { event.preventDefault(); onDone() }}>
    <fieldset>
      <legend>Sort by</legend>
      <div className="catalog-filter-controls">
        <Segmented
          name="catalog-sort"
          label="Sort hosts"
          value={query.sort}
          options={SORT_KEYS.map((key) => ({ value: key, label: sortKeyLabel(key) }))}
          onChange={(key) => onChange(withSortKey(query, key))}
        />
        <Segmented<SortDirection>
          name="catalog-direction"
          label="Sort direction"
          value={query.direction}
          options={[{ value: 'asc', label: labels.asc }, { value: 'desc', label: labels.desc }]}
          onChange={(direction) => onChange({ ...query, direction })}
        />
      </div>
    </fieldset>
    <fieldset>
      <legend>Location</legend>
      <div className="catalog-filter-controls catalog-filter-row">
        <label className="catalog-filter-select">Region<select value={query.region} onChange={(event) => onChange({ ...query, region: event.target.value })}><option value="">All regions</option>{regions.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="catalog-filter-select">Tier<select value={query.tier} onChange={(event) => onChange({ ...query, tier: event.target.value })}><option value="">All tiers</option>{tiers.map((item) => <option key={item}>{item}</option>)}</select></label>
      </div>
    </fieldset>
    <fieldset>
      <legend>At least</legend>
      <div className="catalog-filter-controls catalog-filter-row">
        <NumberFilter id="catalog-min-cpu" key={`minCpu-${resetEpoch}`} field="minCpu" query={query} onChange={onChange} aria-label="Minimum CPU" inputMode="decimal" suffix="cores" />
        <NumberFilter id="catalog-min-ram" key={`minRam-${resetEpoch}`} field="minRam" query={query} onChange={onChange} aria-label="Minimum RAM" inputMode="numeric" suffix="GB RAM" />
      </div>
    </fieldset>
    <fieldset>
      <legend>GPU</legend>
      <div className="catalog-filter-controls">
        <label htmlFor="catalog-gpu-model" className="sr-only">GPU model</label>
        {/* A substring of the model name, not a count: it was labelled
            "Minimum GPU" and matched "A100" in "NVIDIA A100 80GB". */}
        <input id="catalog-gpu-model" className="catalog-filter-text" value={query.gpuModel} placeholder="e.g. A100" autoComplete="off" aria-describedby="catalog-gpu-model-help" onChange={(event) => onChange({ ...query, gpuModel: event.target.value })} />
        <span className="field-help" id="catalog-gpu-model-help">Matches hosts whose GPU model contains this text.</span>
      </div>
    </fieldset>
    <fieldset>
      <legend>Trust</legend>
      <div className="catalog-filter-controls">
        <div className="catalog-filter-row">
          <label htmlFor="catalog-track">Track record ≥</label>
          <NumberFilter id="catalog-track" key={`track-${resetEpoch}`} field="track" query={query} onChange={onChange} inputMode="numeric" suffix="%" describedBy="catalog-track-help" />
        </div>
        <span className="field-help" id="catalog-track-help">Unrated hosts are hidden once you set this.</span>
        <label className="switch-row catalog-filter-switch">
          <input
            type="checkbox"
            role="switch"
            aria-label="Verified only"
            checked={query.verified}
            disabled={!auditDataAvailable}
            aria-describedby={auditDataAvailable ? undefined : 'catalog-verified-help'}
            onChange={(event) => onChange({ ...query, verified: event.target.checked })}
          />
          <span>Verified hosts only</span>
        </label>
        {!auditDataAvailable && <span className="field-help" id="catalog-verified-help">Verification status is unavailable right now.</span>}
      </div>
    </fieldset>
    <div className="catalog-filter-footer">
      {/* Reset all is the only control that also resets the sort; Clear
          filters, beside the chips, keeps it. */}
      <Button type="button" variant="ghost" onClick={() => onReset(DEFAULT_QUERY)}>Reset all</Button>
      <Button type="submit" variant="primary">Show {pluralHosts(resultCount)}</Button>
    </div>
  </form>
}

/*
 * Only the checked option is tabbable — the ARIA radio-group pattern. Native
 * radios already behave so for Tab, but a focus trap's "first tabbable" (the
 * sheet's autofocus) reads `tabIndex`, and without this it landed on the first
 * option whatever was selected. Arrow keys still move within the group.
 */
function Segmented<Value extends string>({ name, label, value, options, onChange }: { name: string; label: string; value: Value; options: Array<{ value: Value; label: string }>; onChange: (value: Value) => void }) {
  return <div className="segmented catalog-segmented" role="radiogroup" aria-label={label}>
    {options.map((option) => <span key={option.value}>
      <input className="sr-only" type="radio" id={`${name}-${option.value}`} name={name} value={option.value} checked={value === option.value} tabIndex={value === option.value ? 0 : -1} onChange={() => onChange(option.value)} />
      <label htmlFor={`${name}-${option.value}`}>{option.label}</label>
    </span>)}
  </div>
}

/**
 * A number typed as text.
 *
 * Not `type="number"`: a scroll over one silently changes the value (the same
 * reason the port fields avoid it). What the tenant typed stays in the field;
 * only a value that parses reaches the query and the URL, and anything else
 * says so instead of quietly emptying the list.
 */
function NumberFilter({ id, field, query, onChange, suffix, describedBy, ...inputProps }: { id: string; field: NumericFilterKey; query: CatalogQuery; onChange: (next: CatalogQuery) => void; suffix: string; describedBy?: string } & Pick<HTMLAttributes<HTMLInputElement>, 'aria-label' | 'inputMode'>) {
  const committed = query[field]
  const [text, setText] = useState(committed)
  // Follow the query when something else changes it — a chip removed — but not
  // when it holds '' because what is typed here does not parse. A reset can
  // leave `committed` at '' unchanged, so it remounts this field by key instead.
  useEffect(() => {
    setText((current) => numericFilterValue(current) === committed ? current : committed)
  }, [committed])
  const invalid = text.trim() !== '' && numericFilterValue(text) === ''
  const errorId = `${id}-error`
  return <span className="catalog-filter-number">
    <span className="catalog-filter-number-input">
      <input
        {...inputProps}
        id={id}
        type="text"
        autoComplete="off"
        placeholder="Any"
        value={text}
        aria-invalid={invalid || undefined}
        aria-describedby={[describedBy, invalid ? errorId : undefined].filter(Boolean).join(' ') || undefined}
        onChange={(event) => {
          setText(event.target.value)
          onChange({ ...query, [field]: numericFilterValue(event.target.value) })
        }}
      />
      <span className="catalog-filter-suffix">{suffix}</span>
    </span>
    {invalid && <span className="field-error" id={errorId}>Enter a number</span>}
  </span>
}

/** The width at which the panel becomes a bottom sheet; the stylesheet uses the same breakpoint. */
export const NARROW_VIEWPORT = '(max-width: 720px)'

/**
 * Whether `query` matches, kept current.
 *
 * CSS alone cannot choose between the inline panel and the sheet: the sheet is
 * a dialog, and whether one is mounted decides where focus goes and what a
 * screen reader can reach.
 */
export function useMediaQuery(query: string): boolean {
  const read = () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches
  const [matches, setMatches] = useState(read)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const list = window.matchMedia(query)
    const update = () => setMatches(list.matches)
    update()
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [query])
  return matches
}
